import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Armchair, CalendarClock, AlertTriangle, CheckCircle2, Wrench, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { CARD, Label, Loader, EmptyState } from "@/components/common";
import { ReportFailureButton } from "@/components/maint/ReportFailure";
import { StatusBadge, FAIL_STATUS, URGENCY, URG_C, LEVEL_C, daysLabel } from "@/components/maint/maintUtils";

function Stat({ icon: Icon, label, value, tone, testid }) {
  return (
    <div className={`${CARD} p-4`} data-testid={testid}>
      <div className="flex items-center gap-2 text-gray-400"><Icon className="h-4 w-4" /><Label>{label}</Label></div>
      <p className={`mt-1 font-display text-2xl font-black sm:text-3xl ${tone}`}>{value}</p>
    </div>
  );
}

function EquipmentCard({ e, onDone }) {
  const next = e.plans.find((p) => p.next_due);
  return (
    <div className="rounded-2xl border border-gray-100 p-4" data-testid={`cab-eq-${e.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <Link to={`/maintenance/equipement/${e.id}`} className="group min-w-0" data-testid={`cab-eq-link-${e.id}`}>
          <p className="flex items-center gap-1 font-semibold group-hover:text-[#065F46]">{e.name}<ChevronRight className="h-4 w-4 text-gray-300" /></p>
          <p className="text-xs text-gray-400">{[e.category, e.brand, e.model].filter(Boolean).join(" · ")}</p>
        </Link>
        <StatusBadge s={e.status} testid={`cab-eq-status-${e.id}`} />
      </div>
      <div className="mt-3 space-y-1.5 text-sm">
        {e.plans.length === 0 ? <p className="text-xs text-gray-400">Aucun entretien planifié.</p> : e.plans.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-gray-600">{p.label}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${LEVEL_C[p.level] || "bg-gray-100 text-gray-500"}`}>
              {p.next_due ? `${fmtDate(p.next_due)} · ${daysLabel(p.days)}` : "date à définir"}
            </span>
          </div>
        ))}
        {!next && e.last_maintenance && <p className="text-xs text-gray-400">Dernière intervention : {fmtDate(e.last_maintenance)}</p>}
      </div>
      {e.open_failures.length > 0 && (
        <div className="mt-3 space-y-1.5 rounded-xl bg-rose-50/60 p-2.5">
          {e.open_failures.map((f) => (
            <div key={f.id} className="text-xs" data-testid={`cab-failure-${f.id}`}>
              <p className="font-semibold text-rose-700">{f.description}</p>
              <p className="text-gray-500">Signalée le {fmtDate(f.date)} · <b>{FAIL_STATUS[f.status] || f.status}</b>
                {f.urgency && <span className={`ml-1 rounded px-1.5 py-0.5 font-semibold ${URG_C[f.urgency]}`}>{URGENCY[f.urgency]}</span>}</p>
            </div>
          ))}
        </div>
      )}
      <div className="mt-3"><ReportFailureButton equipmentId={e.id} onDone={onDone} className="!px-4 !py-2 !text-xs" /></div>
    </div>
  );
}

function LocationBlock({ loc, onDone }) {
  return (
    <div className={`${CARD} p-4 sm:p-6`} data-testid="cab-location">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="font-display text-lg font-bold">{loc.piece}</h2>
        {loc.site_name && <span className="text-sm text-gray-400">· {loc.site_name}</span>}
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${loc.shared ? "bg-sky-50 text-sky-700" : "bg-emerald-50 text-emerald-700"}`}>{loc.shared ? "Pièce commune" : "Mon cabinet"}</span>
      </div>
      {loc.equipment.length === 0 ? <p className="text-sm text-gray-400">Aucun appareil enregistré dans cette pièce.</p> :
        <div className="grid gap-3 md:grid-cols-2">{loc.equipment.map((e) => <EquipmentCard key={e.id} e={e} onDone={onDone} />)}</div>}
    </div>
  );
}

export default function MonCabinet() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const load = useCallback(() => { api.get("/maint/my-cabinet").then((r) => setData(r.data)).catch(() => setErr(true)); }, []);
  useEffect(() => { load(); }, [load]);

  if (err) return <EmptyState title="Profil praticien non lié" subtitle="Votre compte n'est associé à aucun praticien. Contactez le propriétaire du cabinet." />;
  if (!data) return <Loader />;
  const s = data.summary;
  const own = [...data.locations].sort((a, b) => Number(a.shared) - Number(b.shared));
  return (
    <div className="space-y-5">
      <div>
        <p className="text-sm text-gray-400">{data.practitioner_name}</p>
        <h1 className="font-display text-2xl font-bold sm:text-3xl">Mon cabinet</h1>
      </div>
      {own.length === 0 ? (
        <EmptyState title="Aucun cabinet associé" subtitle="Le propriétaire peut associer vos cabinets et les pièces communes depuis votre fiche praticien (Rétrocessions)." />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat icon={Armchair} label="Appareils" value={s.total} tone="text-gray-900" testid="cab-stat-total" />
            <Stat icon={CheckCircle2} label="Opérationnels" value={s.operationnel} tone="text-emerald-700" testid="cab-stat-ok" />
            <Stat icon={AlertTriangle} label="Pannes en cours" value={s.pannes_ouvertes} tone={s.pannes_ouvertes ? "text-rose-600" : "text-gray-900"} testid="cab-stat-failures" />
            <Stat icon={CalendarClock} label="Entretiens en retard" value={s.retard} tone={s.retard ? "text-rose-600" : "text-gray-900"} testid="cab-stat-late" />
          </div>
          {data.next_plans.length > 0 && (
            <div className={`${CARD} p-4 sm:p-6`} data-testid="cab-next-plans">
              <div className="mb-2 flex items-center gap-2 text-gray-400"><Wrench className="h-4 w-4" /><Label>Prochains entretiens et contrôles</Label></div>
              <div className="divide-y divide-gray-50">
                {data.next_plans.map((p) => (
                  <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm">
                    <span><b>{p.equipment_name}</b> <span className="text-gray-500">· {p.label}</span></span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${LEVEL_C[p.level] || ""}`}>{fmtDate(p.next_due)} · {daysLabel(p.days)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {own.map((loc) => <LocationBlock key={`${loc.site_id}-${loc.piece}`} loc={loc} onDone={load} />)}
        </>
      )}
    </div>
  );
}
